import { useTranslation } from 'react-i18next';

import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';

export interface StatusPillOption {
  /** Filter value sent to the query (e.g. a status/state enum member). */
  value: string;
  /** Already-translated label. */
  label: string;
  /**
   * Full literal Tailwind "active" classes for the selected pill, e.g.
   * `'data-[state=on]:bg-emerald-500'`. Must be a literal string (not built at
   * runtime) so Tailwind can generate it. `text-white` is added automatically.
   */
  activeClassName: string;
}

interface StatusFilterPillsProps {
  /** Current selected value (undefined/null = "All"). */
  value: string | undefined | null;
  /** Called with the new value, or `undefined` when "All" / the active pill is toggled off. */
  onChange: (value: string | undefined) => void;
  options: StatusPillOption[];
  /** Label for the leading "All" pill (defaults to `t('all')`). */
  allLabel?: string;
  className?: string;
}

/**
 * Always-visible rounded status/state pill filter (single-select), matching the
 * Contracts/Matters status filter. Renders an "All" pill plus one colored pill
 * per option; the selected pill fills with its color.
 */
export function StatusFilterPills({
  value,
  onChange,
  options,
  allLabel,
  className,
}: StatusFilterPillsProps) {
  const { t } = useTranslation();

  return (
    <ToggleGroup
      type="single"
      size="sm"
      className={cn('flex-wrap justify-start gap-1', className)}
      value={value ?? 'none'}
      onValueChange={(val) => onChange(val === 'none' || val === '' ? undefined : val)}
    >
      <ToggleGroupItem
        value="none"
        aria-label={allLabel ?? t('all')}
        className="rounded-full border px-4 data-[state=on]:bg-primary data-[state=on]:text-white"
      >
        {allLabel ?? t('all')}
      </ToggleGroupItem>
      {options.map((opt) => (
        <ToggleGroupItem
          key={opt.value}
          value={opt.value}
          aria-label={opt.label}
          className={cn('rounded-full border px-4 data-[state=on]:text-white', opt.activeClassName)}
        >
          {opt.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
